/**
 * Aviso de publicação nova.
 *
 * O service worker novo toma o controlo sozinho (skipWaiting + clients.claim),
 * mas o ecrã que já está aberto continua com o JavaScript que carregou. Sem
 * este aviso a pessoa fica na versão anterior até descobrir o recarregamento
 * forçado.
 *
 * O aviso só aparece quando o identificador anunciado é outro que não o deste
 * bundle. Quem já recebeu o HTML novo (o documento nunca fica em cache) não
 * deve ver o balão só porque o worker acabou de tomar o controlo: os dois
 * identificadores são o mesmo.
 *
 * «Agora não» esconde o balão até à publicação seguinte, sem recarregar. Quem
 * está a meio de um formulário não perde o que escreveu.
 *
 * Recarregar não mexe na sessão. O access token e o utilizador estão no
 * localStorage, o refresh token é um cookie httpOnly, e um reload normal
 * preserva os dois. Não se chama logout, não se faz localStorage.clear, e
 * não se apagam chaves.
 */

import './actualizacao.css';

const MARCA = 'dozy-sw-recarregou';
const ADIADA = 'dozy-versao-adiada';
const CHAVE_VERSAO = 'dozy-version-json-em';
const JANELA_MS = 15000;
const INTERVALO_VERSAO_MS = 15 * 60 * 1000;
const SEM_CARIMBO = '__DOZY_BUILD_ID__';

// O `npm start` não carimba. Sem identificador não há com que comparar, e
// avisar seria um falso alarme em desenvolvimento.
export const BUILD_A_CORRER = process.env.REACT_APP_BUILD_ID || '';

export function versaoUtil(id) {
  const s = String(id || '').trim();
  if (!s || s === SEM_CARIMBO || s === 'dev') return '';
  return s;
}

export function versoesDiferem(aCorrer, anunciada) {
  const a = versaoUtil(aCorrer);
  const b = versaoUtil(anunciada);
  if (!a || !b) return false;
  return a !== b;
}

export function recarregouHaPouco(armazenamento, agora = Date.now()) {
  try {
    const t = Number(armazenamento.getItem(MARCA));
    return Number.isFinite(t) && t > 0 && agora - t < JANELA_MS;
  } catch {
    return false;
  }
}

export function versaoJsonEmEspera(armazenamento, agora = Date.now()) {
  try {
    const t = Number(armazenamento.getItem(CHAVE_VERSAO));
    return Number.isFinite(t) && t > 0 && agora - t < INTERVALO_VERSAO_MS;
  } catch {
    return false;
  }
}

export function foiAdiada(armazenamento, anunciada) {
  try {
    const id = versaoUtil(anunciada);
    return Boolean(id) && armazenamento.getItem(ADIADA) === id;
  } catch {
    return false;
  }
}

export function deveAvisarNovaVersao({ aCorrer, anunciada, recarregouAgora, adiada }) {
  if (recarregouAgora || adiada) return false;
  return versoesDiferem(aCorrer, anunciada);
}

export function aplicarActualizacao(alvo = window) {
  try { sessionStorage.setItem(MARCA, String(Date.now())); } catch { /* o reload segue na mesma */ }
  alvo.location.reload();
}

export function adiarActualizacao(anunciada, armazenamento = localStorage) {
  const id = versaoUtil(anunciada);
  if (!id) return;
  try { armazenamento.setItem(ADIADA, id); } catch { /* esconde-se à mesma */ }
}

export function mostrarAvisoActualizacao(alvo = window, anunciada = '') {
  const doc = alvo.document;
  if (!doc || doc.getElementById('dozy-actualizacao')) return;
  const armazenamento = alvo.localStorage || localStorage;
  const barra = doc.createElement('div');
  barra.id = 'dozy-actualizacao';
  barra.className = 'dozy-actualizacao';
  barra.setAttribute('role', 'status');
  barra.setAttribute('aria-live', 'polite');
  const texto = doc.createElement('span');
  texto.className = 'dozy-actualizacao-texto';
  const curta = doc.createElement('span');
  curta.textContent = 'Nova versão';
  const resto = doc.createElement('span');
  resto.className = 'dozy-actualizacao-resto';
  resto.textContent = ' disponível';
  texto.append(curta, resto);
  const actualizar = doc.createElement('button');
  actualizar.type = 'button';
  actualizar.textContent = 'Atualizar';
  actualizar.addEventListener('click', () => {
    actualizar.disabled = true;
    aplicarActualizacao(alvo);
  });
  const adiar = doc.createElement('button');
  adiar.type = 'button';
  adiar.className = 'dozy-actualizacao-adiar';
  adiar.textContent = 'Agora não';
  adiar.addEventListener('click', () => {
    adiarActualizacao(anunciada, armazenamento);
    barra.remove();
  });
  barra.append(texto, actualizar, adiar);
  doc.body.appendChild(barra);
}

function avaliarAnuncio(anunciada) {
  const recarregouAgora = recarregouHaPouco(sessionStorage);
  const adiada = foiAdiada(localStorage, anunciada);
  if (!deveAvisarNovaVersao({
    aCorrer: BUILD_A_CORRER,
    anunciada,
    recarregouAgora,
    adiada,
  })) return;
  mostrarAvisoActualizacao(window, anunciada);
}

function verVersaoPublicada() {
  // Segundo canal, para quando o worker ainda não mudou de bytes ou o browser
  // ainda não o foi buscar. O ficheiro não entra em cache: senão comparávamos
  // com a publicação anterior. Um separador que acorda muitas vezes não pede
  // isto de cada vez: quinze minutos chegam para saber que houve publicação.
  if (versaoJsonEmEspera(sessionStorage)) return;
  try { sessionStorage.setItem(CHAVE_VERSAO, String(Date.now())); } catch { /* pede-se à mesma */ }
  fetch('/version.json', { cache: 'no-store', credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : null))
    .then((corpo) => { if (corpo && corpo.build) avaliarAnuncio(corpo.build); })
    .catch(() => {});
}

export function iniciarActualizacao() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  // A mensagem traz o identificador do worker que acabou de activar. O
  // controllerchange, sozinho, disparava o balão em quem já tinha o HTML novo.
  navigator.serviceWorker.addEventListener('message', (evento) => {
    const dados = evento.data || {};
    if (dados.tipo === 'NOVA_VERSAO') avaliarAnuncio(dados.build);
  });

  const registar = () => {
    navigator.serviceWorker.register('/service-worker.js', { updateViaCache: 'none' })
      .then((reg) => {
        if (reg.waiting && navigator.serviceWorker.controller) {
          reg.waiting.postMessage({ tipo: 'SKIP_WAITING' });
        }
        const procurar = () => {
          reg.update().catch(() => {});
          verVersaoPublicada();
        };
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') procurar();
        });
        // Um separador deixado aberto o dia todo não faz navegações, e o
        // browser só procura um worker novo quando há uma. Uma hora chega.
        setInterval(procurar, 60 * 60 * 1000);
        verVersaoPublicada();
      })
      .catch(() => {
        // Sem service worker a app funciona na mesma, só sem instalar nem push.
      });
  };

  if (document.readyState === 'complete') registar();
  else window.addEventListener('load', registar);
}
